import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";
import { DemoVideoCard } from "./LandingPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SampleProfilePage — public /demo route.
 * Shows a full player development profile using values from GET /api/demo/config.
 * No login required. Uploaded demo videos are streamed via /api/demo/videos/{id}/file.
 */
export default function SampleProfilePage() {
  const { lang } = useLang();
  const es = lang === "es";
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/demo/config`).then((r) => setCfg(r.data));
  }, []);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#080808] text-white">
        <TopBar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-neutral-500">…</div>
      </div>
    );
  }

  const skillBars = cfg.skill_bars || [];
  const timeline = cfg.timeline || [];
  const library = cfg.library_categories || [];
  const strengths = es ? cfg.strengths_es : cfg.strengths_en;
  const areas = es ? cfg.areas_to_improve_es : cfg.areas_to_improve_en;
  const feedback = es ? cfg.coach_feedback_es : cfg.coach_feedback_en;
  const nextGoal = es ? cfg.next_goal_es : cfg.next_goal_en;

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link
          to="/"
          data-testid="demo-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#e8559a] transition-colors mb-4"
        >
          ← {es ? "Volver al inicio" : "Back to home"}
        </Link>

        {/* Sample banner */}
        <div
          data-testid="demo-sample-banner"
          className="mb-5 rounded-xl px-4 py-3 text-xs uppercase tracking-[0.25em] bg-[#e8559a]/10 border border-[#e8559a]/40 text-[#e8559a] flex items-center gap-2"
        >
          ★ {es ? "EJEMPLO DE DESARROLLO DE JUGADOR" : "SAMPLE PLAYER DEVELOPMENT"}{" "}
          <span className="text-neutral-500 normal-case tracking-normal text-[11px]">
            · {es ? "Los jugadores reales tienen su propio portal privado." : "Real players have their own private portal."}
          </span>
        </div>

        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-5 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-[#e8559a]/10 border border-[#e8559a]/40 flex items-center justify-center">
              <span className="font-bebas text-4xl sm:text-5xl text-[#e8559a] leading-none">
                ★
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80 mb-1">
                {es ? "DESARROLLO DEL JUGADOR" : "PLAYER DEVELOPMENT"}
              </div>
              <h1 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight">
                {cfg.sample_player_name}
              </h1>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300">
                  ⚽ {es ? cfg.sample_player_position_es : cfg.sample_player_position_en}
                </span>
                {cfg.sample_player_age && (
                  <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300">
                    🎂 {cfg.sample_player_age} {es ? "años" : "yrs"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Before / After side-by-side */}
        <section className="mb-6">
          <SectionTitle>
            🎬 {es ? "ANTES VS DESPUÉS" : "BEFORE VS AFTER"}
          </SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DemoVideoCard
              label={es ? "ANTES" : "BEFORE"}
              week={cfg.before_date_label}
              videoId={cfg.before_video_id}
              accent="border-neutral-500/40 text-neutral-400"
            />
            <DemoVideoCard
              label={es ? "DESPUÉS" : "AFTER"}
              week={cfg.after_date_label}
              videoId={cfg.after_video_id}
              accent="border-[#e8559a]/50 text-[#e8559a]"
            />
          </div>
        </section>

        {/* Coaching Indicators */}
        <section className="mb-6 rounded-2xl bg-[#161616] border border-[#252525] p-5 sm:p-6">
          <SectionTitle>
            📈 {es ? "INDICADORES DE COACHING" : "COACHING INDICATORS"}
          </SectionTitle>
          <p className="text-[11px] text-neutral-500 mb-4 -mt-2">
            {es
              ? "No son puntajes científicos. Son indicadores visuales del progreso del jugador."
              : "Not scientific scores. They are visual indicators of the player's development."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {skillBars.map((b, i) => (
              <SkillRow key={i} name={es ? b.name_es : b.name_en} value={b.value} />
            ))}
          </div>
        </section>

        {/* Coach feedback + Strengths + Areas + Next goal */}
        <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FeedbackBlock
            emoji="💬"
            label={es ? "FEEDBACK DEL COACH" : "COACH FEEDBACK"}
            text={feedback}
            accent="text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
          />
          <FeedbackBlock
            emoji="🚀"
            label={es ? "PRÓXIMO OBJETIVO" : "NEXT TRAINING GOAL"}
            text={nextGoal}
            accent="text-[#e8559a] border-[#e8559a]/30 bg-[#e8559a]/5"
          />
          <FeedbackBlock
            emoji="✅"
            label={es ? "FORTALEZAS" : "PLAYER STRENGTHS"}
            text={strengths}
            accent="text-sky-300 border-sky-500/30 bg-sky-500/5"
          />
          <FeedbackBlock
            emoji="🎯"
            label={es ? "ÁREAS A MEJORAR" : "AREAS TO IMPROVE"}
            text={areas}
            accent="text-amber-300 border-amber-500/30 bg-amber-500/5"
          />
        </section>

        {/* Training Journey Timeline */}
        <section className="mb-6">
          <SectionTitle>
            📅 {es ? "RECORRIDO DE ENTRENAMIENTO" : "TRAINING JOURNEY"}
          </SectionTitle>
          <div className="space-y-4">
            {timeline.map((e, i) => (
              <TimelineEntry key={i} entry={e} es={es} index={i} />
            ))}
          </div>
        </section>

        {/* Training Library */}
        <section className="mb-8 rounded-2xl bg-[#161616] border border-[#252525] p-5 sm:p-6">
          <SectionTitle>
            📚 {es ? "LIBRERÍA DE VIDEOS DE ENTRENAMIENTO" : "TRAINING VIDEO LIBRARY"}
          </SectionTitle>
          <p className="text-xs text-neutral-500 mb-4 -mt-2">
            {es
              ? "Los videos de tu hijo se guardan aquí para que puedas ver su desarrollo con el tiempo."
              : "Your child's videos stay here so you can see their development over time."}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {library.map((cat, i) => (
              <div
                key={i}
                className="rounded-xl bg-[#0f0f0f] border border-[#252525] px-3 py-4 text-center hover:border-[#e8559a]/40 transition-colors"
              >
                <div className="text-2xl mb-1.5">🎬</div>
                <div className="text-sm font-medium text-white">
                  {es ? cat.es : cat.en}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="rounded-3xl bg-gradient-to-br from-[#e8559a]/10 to-transparent border border-[#e8559a]/30 p-6 sm:p-10 text-center">
          <h2 className="font-bebas text-3xl sm:text-5xl tracking-[0.03em] leading-tight">
            {es
              ? "ESTE PODRÍA SER EL PORTAL DE TU HIJO."
              : "THIS COULD BE YOUR CHILD'S PORTAL."}
          </h2>
          <p className="mt-3 text-neutral-300 max-w-xl mx-auto">
            {es
              ? "Únete a la Tropa y comienza a documentar su desarrollo desde el primer entrenamiento."
              : "Join the Troop and start documenting their development from the very first training."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              data-testid="demo-cta-register"
              className="px-6 py-3.5 rounded-xl bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-lg tracking-[0.2em] transition-colors shadow-lg shadow-[#e8559a]/30"
            >
              {es ? "REGISTRAR JUGADOR" : "REGISTER PLAYER"} →
            </Link>
            <Link
              to="/programs"
              className="px-6 py-3.5 rounded-xl bg-[#161616] border border-[#e8559a]/40 hover:border-[#e8559a] text-white font-bebas text-lg tracking-[0.2em] transition-colors"
            >
              {es ? "VER PROGRAMAS" : "VIEW PROGRAMS"}
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#1a1a1a] mt-8 py-8 text-center text-xs text-neutral-500">
        SOCCER TROOP · @soccer.troop · Denver, Colorado · Est. 2022 · Train · Focus · Score
      </footer>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-bebas text-2xl sm:text-3xl tracking-[0.15em] text-white mb-4">
      {children}
    </h2>
  );
}

function SkillRow({ name, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="uppercase tracking-[0.15em] text-neutral-300">{name}</span>
        <span className="text-[#e8559a] font-bebas tracking-widest">{v}</span>
      </div>
      <div className="h-2 bg-[#0a0a0a] border border-[#252525] rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#e8559a] to-[#c9407f] rounded-full transition-all duration-1000"
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}

function FeedbackBlock({ emoji, label, text, accent }) {
  if (!text) return null;
  return (
    <div className={`rounded-2xl border p-5 ${accent}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 mb-2">
        {emoji} {label}
      </div>
      <div className="text-sm sm:text-base text-white leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function TimelineEntry({ entry, es, index }) {
  const label = es ? entry.label_es : entry.label_en;
  const week = es ? entry.week_es : entry.week_en;
  const skill = es ? entry.skill_es : entry.skill_en;
  const notes = es ? entry.notes_es : entry.notes_en;
  const isBefore = (entry.label_en || "").toUpperCase() === "BEFORE";
  const isAfter = (entry.label_en || "").toUpperCase() === "AFTER";
  const accent = isAfter
    ? "border-[#e8559a]/50 text-[#e8559a]"
    : isBefore
    ? "border-neutral-500/40 text-neutral-400"
    : "border-sky-500/40 text-sky-300";

  return (
    <div
      data-testid={`timeline-entry-${index}`}
      className="rounded-2xl bg-[#161616] border border-[#252525] overflow-hidden"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <DemoVideoCard
          label={label}
          week={week}
          videoId={entry.video_id}
          accent={accent}
        />
        <div className="p-5 flex flex-col justify-center gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#e8559a]/80 mb-1">
              🎯 {es ? "Habilidad trabajada" : "Skill worked"}
            </div>
            <div className="text-white font-bebas text-2xl tracking-widest">
              {skill}
            </div>
          </div>
          {notes && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                💬 {es ? "Notas del coach" : "Coach notes"}
              </div>
              <div className="text-sm text-neutral-200 leading-relaxed">
                {notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
